/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemMembershipRentalMaybe from './LineItemMembershipRentalMaybe';
import LineItemMemberDiscountMaybe from './LineItemMemberDiscountMaybe';
import LineItemMemberAllowanceMaybe from './LineItemMemberAllowanceMaybe';
import LineItemReturningRenterDiscountMaybe from './LineItemReturningRenterDiscountMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    discountPercent,
    discountCode,
    dayBasedPricing,
    dateBasedPricing,
    rentalLocation,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const lineItems = transaction.attributes.lineItems;
  const unitLineItem = lineItems?.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  // Member bookings on the provider view show ONLY the pool payout — the
  // host should never see the customer's "would-have-paid" rental total or
  // any of the offset accounting that nets to $0. Standard rental row,
  // SubTotal, and Refund are all suppressed; LineItemMembershipRentalMaybe
  // and LineItemTotalPrice render the pool view (and handle cancellation
  // by showing a refund of the pool payout instead).
  const isMemberBookingTx = lineItems.some(
    li => typeof li.code === 'string' && li.code.startsWith('line-item/membership-host-offset-')
  );
  const hideForMemberProvider = isMemberBookingTx && isProvider;
  const hideRentalRow = hideForMemberProvider;

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />
      {hideRentalRow ? null : (
        <LineItemBasePriceMaybe
          booking={booking}
          lineItems={lineItems}
          code={lineItemUnitType}
          intl={intl}
          dayBasedPricing={dayBasedPricing}
          dateBasedPricing={dateBasedPricing}
          rentalLocation={rentalLocation}
        />
      )}
      <LineItemMembershipRentalMaybe lineItems={lineItems} intl={intl} isProvider={isProvider} />
      <LineItemMemberDiscountMaybe lineItems={lineItems} intl={intl} isProvider={isProvider} />
      <LineItemMemberAllowanceMaybe lineItems={lineItems} isProvider={isProvider} />
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      {hideForMemberProvider ? null : (
        <LineItemSubTotalMaybe
          lineItems={lineItems}
          code={lineItemUnitType}
          userRole={userRole}
          intl={intl}
          marketplaceCurrency={currency}
        />
      )}
      
      {/* Delivery charges (customer or host set) */}
      {transaction?.attributes?.metadata?.delivery?.deliveryAmount ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.deliveryCharge" />
          </span>
          <span className={css.itemValue}>
            ${(transaction.attributes.metadata.delivery.deliveryAmount / 100).toFixed(2)}
          </span>
        </div>
      ) : null}
      
      {transaction?.attributes?.metadata?.pickup?.pickupAmount ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.pickupCharge" />
          </span>
          <span className={css.itemValue}>
            ${(transaction.attributes.metadata.pickup.pickupAmount / 100).toFixed(2)}
          </span>
        </div>
      ) : null}
      
      {/* Processing fee for delivery/pickup charges (10% + tax) */}
      {(transaction?.attributes?.metadata?.delivery?.deliveryAmount || 
        transaction?.attributes?.metadata?.pickup?.pickupAmount) && isCustomer ? (() => {
        const deliveryAmount = transaction.attributes.metadata.delivery?.deliveryAmount || 0;
        const pickupAmount = transaction.attributes.metadata.pickup?.pickupAmount || 0;
        const totalCharges = (deliveryAmount + pickupAmount) / 100;
        const processingFee = totalCharges * 0.23; // 10% + tax
        
        return (
          <div className={css.lineItem}>
            <span className={css.itemLabel}>
              <FormattedMessage id="OrderBreakdown.deliveryProcessingFee" />
            </span>
            <span className={css.itemValue}>
              ${processingFee.toFixed(2)}
            </span>
          </div>
        );
      })() : null}

      {/* Supplementary charge (dispute) - show base amount and fee separately */}
      {transaction?.attributes?.metadata?.dispute?.dollarAmount ? (() => {
        const baseAmount = transaction.attributes.metadata.dispute.dollarAmount / 100;
        const feeAmount = baseAmount * 0.23;
        
        return (
          <>
            <div className={css.lineItem}>
              <span className={css.itemLabel}>
                <FormattedMessage id="OrderBreakdown.supplementaryCharge" />
              </span>
              <span className={css.itemValue}>
                ${baseAmount.toFixed(2)}
              </span>
            </div>
            {isCustomer && (
              <div className={css.lineItem}>
                <span className={css.itemLabel}>
                  <FormattedMessage id="OrderBreakdown.supplementaryChargeFee" />
                </span>
                <span className={css.itemValue}>
                  ${feeAmount.toFixed(2)}
                </span>
              </div>
            )}
          </>
        );
      })() : null}
      
      {hideForMemberProvider ? null : (
        <LineItemRefundMaybe
          lineItems={lineItems}
          intl={intl}
          marketplaceCurrency={currency}
          userRole={userRole}
        />
      )}

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
        transaction={transaction}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemReturningRenterDiscountMaybe
        lineItems={lineItems}
        intl={intl}
        isProvider={isProvider}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        discountCode={discountCode}
        discountPercent={discountPercent}
        intl={intl}
        transaction={transaction}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
